<template>
  <section id="schools" class="schools-section">
    <div class="container">
      <div class="section-head">
        <h2 class="title">{{ $t('schools.title') }}</h2>
        <p class="subtitle">{{ $t('schools.subtitle') }}</p>
      </div>

      <transition-group 
        ref="gridRef"
        name="staggered-fade" 
        tag="div" 
        class="cards-grid"
      >
        <article
          v-for="(school, index) in visibleSchools"
          :key="school.name || index"
          class="school-card"
        >
          <!-- Decorative Music Note SVG Background -->
          <div class="card-decoration">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
              <path d="M9 18V5l12-2v13" />
              <circle cx="6" cy="18" r="3" />
              <circle cx="18" cy="16" r="3" />
            </svg>
          </div>

          <div class="card-content">
            <header class="card-header">
              <h3 class="school-name">{{ school.name }}</h3>
              <div class="address-row">
                <!-- Pin Icon -->
                <svg class="icon-pin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                <span class="address-text">{{ school.address }}</span>
              </div>
            </header>

            <div class="card-body">
              <h4 class="label-small">{{ $t('schools.groupsLabel') }}</h4>
              <ul class="group-list">
                <li v-for="(group, idx) in school.groups" :key="idx" class="group-item">
                  <span class="bullet">•</span>
                  {{ group }}
                </li>
              </ul>
            </div>

            <footer class="card-footer">
              <div class="badge frequency">
                <!-- Clock Icon -->
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                <span>{{ school.frequency }}</span>
              </div>
              <div v-if="school.note" class="badge note">
                <!-- Info Icon -->
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
                <span>{{ school.note }}</span>
              </div>
            </footer>
          </div>
        </article>
      </transition-group>

      <div v-if="hasMore" class="actions-wrapper">
        <button class="btn-toggle" @click="toggleShowAll">
          <span class="btn-text">
            {{ showAll ? $t('schools.actions.showLess') : $t('schools.actions.showMore') }}
          </span>
          <svg
            class="icon-chevron"
            :class="{ 'is-flipped': showAll }"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

const { tm } = useI18n();

const schools = computed(() => tm('schools.items') || []);
const showAll = ref(false);

// Refs for dynamic calculation
const gridRef = ref(null);
const itemsPerRow = ref(3); // Default value before calculation

// Calculate how many items fit in one row based on container width
const calculateItemsPerRow = () => {
  if (!gridRef.value?.$el) return;
  
  const containerWidth = gridRef.value.$el.clientWidth;
  const gap = 24; // Must match CSS gap
  const minCardWidth = 320; // Must match CSS grid minmax
  
  // Formula: (Container + Gap) / (Card + Gap)
  const columns = Math.floor((containerWidth + gap) / (minCardWidth + gap));
  
  // Ensure we show at least 1, and update the ref
  itemsPerRow.value = Math.max(1, columns);
};

let resizeObserver;

onMounted(() => {
  // Initial calculation
  calculateItemsPerRow();

  // Watch for resize events to update the count dynamically
  if (gridRef.value?.$el) {
    resizeObserver = new ResizeObserver(() => {
      calculateItemsPerRow();
    });
    resizeObserver.observe(gridRef.value.$el);
  }
});

onUnmounted(() => {
  if (resizeObserver) resizeObserver.disconnect();
});

const hasMore = computed(() => (schools.value?.length || 0) > itemsPerRow.value);

const visibleSchools = computed(() => {
  const list = schools.value || [];
  return showAll.value ? list : list.slice(0, itemsPerRow.value);
});

const toggleShowAll = () => {
  showAll.value = !showAll.value;
};
</script>

<style scoped>
/* Variables for easy theming */
.schools-section {
  --primary-red: var(--primary-color);
  --primary-red-soft: #fbeaec;
  --text-dark: var(--secondary-color);
  --text-gray: var(--muted-color);
  --bg-gradient: linear-gradient(180deg, #fff 0%, #fbfaf8 100%);
  --card-bg: #ffffff;
  --card-shadow: var(--shadow-sm);
  --card-shadow-hover: var(--shadow-md);
  --radius-lg: var(--radius);
  --radius-full: 9999px;
  
  padding: clamp(76px, 8vw, 110px) 20px;
  background: var(--bg-gradient);
  font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}

.container {
  max-width: var(--content-width);
  margin: 0 auto;
}

/* Header Styling */
.section-head {
  text-align: center;
  margin-bottom: 54px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.title {
  font-size: clamp(2rem, 3.4vw, 3rem);
  font-weight: 750;
  color: var(--text-dark);
  margin: 0 0 18px;
  letter-spacing: -0.035em;
}

.title-underline {
  width: 60px;
  height: 4px;
  background: var(--primary-red);
  border-radius: 2px;
  margin-bottom: 16px;
}

.subtitle {
  font-size: 1.1rem;
  color: var(--text-gray);
  max-width: 600px;
  margin: 0;
  line-height: 1.6;
}

/* Grid Layout */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 24px;
  margin-bottom: 40px;
}

/* Card Styling */
.school-card {
  background: var(--card-bg);
  border-radius: var(--radius-lg);
  box-shadow: var(--card-shadow);
  border: 1px solid var(--border-color);
  overflow: hidden;
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
}

.school-card:hover {
  transform: translateY(-3px);
  box-shadow: var(--card-shadow-hover);
  border-color: rgba(211, 47, 47, 0.2);
}

.card-decoration {
  position: absolute;
  top: -10px;
  right: -10px;
  width: 100px;
  height: 100px;
  opacity: 0.03;
  color: var(--primary-red);
  pointer-events: none;
  transform: rotate(15deg);
  transition: opacity 0.3s;
}

.school-card:hover .card-decoration {
  opacity: 0.08;
}

.card-content {
  padding: 30px;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 2;
}

/* Card Header */
.card-header {
  margin-bottom: 20px;
}

.school-name {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--text-dark);
  margin: 0 0 12px;
  line-height: 1.35;
}

.address-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: var(--text-gray);
  font-size: 0.95rem;
}

.icon-pin {
  flex-shrink: 0;
  margin-top: 3px;
  color: var(--primary-red);
}

/* Groups List */
.card-body {
  flex-grow: 1;
  margin-bottom: 24px;
}

.label-small {
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.05em;
  color: #94a3b8;
  margin: 0 0 10px;
  font-weight: 600;
}

.group-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.group-item {
  font-size: 0.95rem;
  color: #475569;
  line-height: 1.5;
  display: flex;
  gap: 8px;
}

.bullet {
  color: var(--primary-red);
  font-weight: bold;
}

/* Card Footer (Badges) */
.card-footer {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: auto;
  padding-top: 20px;
  border-top: 1px solid #f1f5f9;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: var(--radius-full);
  font-size: 0.85rem;
  font-weight: 500;
  line-height: 1.2;
}

.badge.frequency {
  background-color: var(--primary-red-soft);
  color: #b91c1c;
}

.badge.note {
  background-color: #f1f5f9;
  color: #475569;
  font-style: italic;
}

/* Show More Button */
.actions-wrapper {
  text-align: center;
  margin-top: 40px;
}

.btn-toggle {
  background: white;
  border: 2px solid var(--primary-red);
  color: var(--primary-red);
  padding: 12px 28px;
  border-radius: var(--radius-full);
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.btn-toggle:hover {
  background: var(--primary-red);
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(211, 47, 47, 0.25);
}

.btn-toggle:active {
  transform: translateY(0);
}

.icon-chevron {
  transition: transform 0.3s ease;
}

.icon-chevron.is-flipped {
  transform: rotate(180deg);
}

/* Transition Animations */
.staggered-fade-enter-active,
.staggered-fade-leave-active {
  transition: all 0.4s ease;
}

.staggered-fade-enter-from,
.staggered-fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }
  
  .title {
    font-size: 1.75rem;
  }
  
  .school-card {
    margin: 0;
  }
  
  .btn-toggle {
    width: 100%;
    justify-content: center;
  }
}
</style>
